import { ApolloClient, Context, ObservableQuery } from '@apollo/client'
import type { DispatchWithoutAction } from 'react'
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  EMPTY,
  from,
  interval,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs'
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators'

import {
  Exact,
  GetCustomerCartDocument,
  GetCustomerCartQuery,
  GetGuestCartDocument,
  GetGuestCartQuery,
  SetCartAttributesDocument,
} from '../../../graphql/magento'
import { isNotNull } from '../../../utils/collectionTools'
import { logAndCaptureException } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { fromObservableQuery } from '../../../utils/rx/observable-query'
import { latestAccessToken$ } from '../../auth/state'
import { latestDispensary$ } from '../../dispensaries/state'
import type { Dispensary } from '../../dispensaries/types'
import { latestApolloClient$ } from '../../graphql/apollo'
import type { CartGetState } from '../reducers/cart-get-state'

/**
 * The whole reducer state, not just the `UseCartGet` projection the cart context exposes.
 *
 * `mergeStatus` is the field that lets a mutation tell a settled cart from one the guest-to-customer
 * merge is still rewriting. Without it the only available gate was `loading`, which narrows the
 * window rather than closing it (sc-142551, sc-142675).
 */
export type CartMirrorState = CartGetState & { refetch: DispatchWithoutAction }

/**
 * Holds the cart state mirrored from `useCartGet`.
 *
 * A `BehaviorSubject` rather than a `bind`/`state`, whose refCount discards the held value when
 * subscribers reach zero. Consumers here are mutations that must read the current cart at the
 * moment they run, so the value has to outlive any gap in subscribers.
 */
const cartStateSubject$ = new BehaviorSubject<CartMirrorState | null>(null)

export const setCartState = (state: CartMirrorState): void => cartStateSubject$.next(state)

/** useCartGet state */
export const cartState$: Observable<CartMirrorState> = cartStateSubject$.pipe(
  filter(isNotNull),
  // compare only the fields consumers read: adding the rest would emit on every reducer transition
  distinctUntilChanged(
    (a, b) =>
      a.cart === b.cart &&
      a.loading === b.loading &&
      a.error === b.error &&
      a.refetch === b.refetch &&
      a.type === b.type &&
      a.mergeStatus === b.mergeStatus &&
      a.guestCartId === b.guestCartId,
  ),
)

/**
 * Get observable query from Apollo client for customer's cart
 */
const getCustomerCartObservableQueryFactory = (
  client: ApolloClient<unknown>,
  token: string,
): ObservableQuery<GetCustomerCartQuery, Exact<{ [key: string]: never }>> =>
  client.watchQuery({
    query: GetCustomerCartDocument,
    context: { token },
    // override global error policy
    errorPolicy: 'none',
  })

const watchGetCustomerCartFactory = (client: ApolloClient<unknown>, token: string) =>
  fromObservableQuery(getCustomerCartObservableQueryFactory(client, token)).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server)
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

/**
 * Combine latest Apollo client with access token and return the customer's cart, watching for updates.
 *
 * NOTE: this does not support guest carts.
 */
export const latestCustomerCart$: Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }
> = combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
  switchMap(([client, token]) => watchGetCustomerCartFactory(client, token)),
)

/**
 * Combine latest Apollo client with access token and refetch the customer's cart once.
 */
export const refetchCustomerCart$ = ({ context }: { context?: Context } = {}): Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }
> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, token]) =>
      from(
        client.query({
          query: GetCustomerCartDocument,
          context: { token, ...context },
          errorPolicy: 'none',
          fetchPolicy: 'network-only',
        }),
      ).pipe(
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      ),
    ),
  )

/**
 * Combine latest Apollo client with access token and refetch the customer's cart with the given interval.
 */
export const refetchCustomerCartWithInterval$ = (
  milliseconds: number,
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, token]) => {
      const obsQuery = getCustomerCartObservableQueryFactory(client, token)
      return interval(milliseconds).pipe(
        switchMap(() => obsQuery.refetch()),
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

/**
 * BehaviorSubject to track the current guest cart ID
 */
export const latestGuestCartId$ = new BehaviorSubject<string | null>(null)

/**
 * Update the guest cart ID observable
 */
export const updateGuestCartId = (cartId: string | null): void => {
  latestGuestCartId$.next(cartId)
}

/**
 * Get observable query from Apollo client for guest cart
 */
const getGuestCartObservableQueryFactory = (
  client: ApolloClient<unknown>,
  cartId: string,
): ObservableQuery<GetGuestCartQuery, Exact<{ cartId: string }>> =>
  client.watchQuery({
    query: GetGuestCartDocument,
    variables: { cartId },
    // override global error policy
    errorPolicy: 'none',
  })

/**
 * Combine latest Apollo client with guest cart ID and refetch the guest cart with the given interval.
 */
export const refetchGuestCartWithInterval$ = (
  milliseconds: number,
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetGuestCartQuery }> =>
  combineLatest([latestApolloClient$, latestGuestCartId$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, cartId]) => {
      const obsQuery = getGuestCartObservableQueryFactory(client, cartId)
      return interval(milliseconds).pipe(
        switchMap(() => obsQuery.refetch()),
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

type SuccessfulCartFetch = {
  _tag: 'Right'
  data: GetCustomerCartQuery | GetGuestCartQuery
}

/**
 * Correct a cart associated with the *wrong* practitioner: when the user is in a dispensary context,
 * align the cart's practitioner code with that dispensary's code (e.g. after shopping across
 * multiple dispensaries). Reads the cart from the just-fetched `result` so the decision uses fresh
 * data. A failed `setCartAttributes` is logged and swallowed so the caller's refetch stream is never
 * torn down. Returns the original `result` either way.
 *
 * We deliberately do NOT clear the practitioner code in global context. Per sc-132622
 * (https://app.shortcut.com/designsforhealth/story/132622#activity-141484): without guest redirects
 * the lingering-code case isn't a concern right now, so a cart that leaves a dispensary keeps its
 * code. This function only ever sets a real dispensary code, never blanks one.
 */
export const reconcileCartPractitionerCode = (
  result: SuccessfulCartFetch,
  client: ApolloClient<unknown>,
  dispensary: Dispensary | null | undefined,
): Observable<SuccessfulCartFetch> => {
  // customer query -> `customerCart`, guest query -> `cart`
  const cart = 'customerCart' in result.data ? result.data.customerCart : result.data.cart

  if (!cart) {
    return of(result)
  }

  // Only reconcile toward a dispensary's code. In global context (no dispensary) — or a dispensary
  // without a code — leave the cart untouched; we never clear the practitioner code (sc-132622).
  const targetPractitionerCode = dispensary?.practitionerCode
  if (!targetPractitionerCode) {
    return of(result)
  }

  // Already correct — nothing to do (and avoids re-firing the mutation on every poll tick).
  if ((cart.practitioner_code ?? '') === targetPractitionerCode) {
    return of(result)
  }

  return from(
    client.mutate({
      mutation: SetCartAttributesDocument,
      variables: {
        cartId: cart.id,
        practitionerCode: targetPractitionerCode,
      },
      // Attach the bearer token for logged-in customers (no-op for guests) so Magento accepts the
      // mutation on a customer's quote; without it the customer-cart reconcile is rejected.
      context: { useLatestTokenIfLoggedIn: true },
    }),
  ).pipe(
    // Re-emit the pre-mutation `result`; its `practitioner_code` is now stale, but the mutation
    // returns the full `...cart` fragment which Apollo normalizes into the cache, so
    // `useCartGet`/`cartState$` consumers stay correct. Future consumers must not trust this field.
    map(() => result),
    // a failed attribute sync must not tear down the cart refetch stream
    catchError((error) => {
      logAndCaptureException(error)
      return of(result)
    }),
  )
}

/**
 * Universal cart refetch with interval that works for both customer and guest carts.
 * Uses customer cart refetch if access token is available, otherwise uses guest cart refetch.
 */
export const refetchCartWithInterval$ = (
  milliseconds: number,
): Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery | GetGuestCartQuery }
> =>
  combineLatest([latestAccessToken$, latestGuestCartId$]).pipe(
    switchMap(([accessToken, guestCartId]) => {
      // If we have an access token, use customer cart refetch
      if (accessToken) {
        return refetchCustomerCartWithInterval$(milliseconds)
      }
      // If we have a guest cart ID, use guest cart refetch
      if (guestCartId) {
        return refetchGuestCartWithInterval$(milliseconds)
      }
      // If neither, return empty observable (no cart to refetch)
      return EMPTY
    }),
    withLatestFrom(latestApolloClient$, latestDispensary$),
    // Reconcile only successful fetches; a `Left` (failed refetch) must pass through untouched so
    // downstream `take(1)` consumers (e.g. useCheckoutFormState's submit path) still get an emission
    // and don't hang. This stream never errors — `coerceCaughtToLeft` already turned failures into
    // `Left` next-notifications upstream.
    concatMap(([result, client, dispensary]) =>
      result._tag === 'Right'
        ? reconcileCartPractitionerCode(result, client, dispensary)
        : of(result),
    ),
  )
